import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/** `sd-16` 처럼 디자인 시스템 간격 스케일을 가리키는 값인지 (p-sd-16 → 'sd-16') */
const isSdSpacing = (value: string) => /^sd-\d+$/.test(value);

/**
 * 간격 스케일은 `--spacing-sd-*` 로 나가므로 클래스가 `p-sd-16` 형태다.
 * tailwind-merge 는 이 값을 모르기 때문에 등록하지 않으면 `p-sd-8` 과 `p-sd-24` 가
 * 둘 다 살아남아 소비자의 className 오버라이드가 조용히 무시된다.
 * (등록하면 소비자가 표준 `p-4` 로도 덮어쓸 수 있다 — 같은 그룹으로 인식되므로.)
 */
const SD_SPACING_GROUPS = [
  'inset',
  'inset-x',
  'inset-y',
  'start',
  'end',
  'top',
  'right',
  'bottom',
  'left',
  'basis',
  'gap',
  'gap-x',
  'gap-y',
  'p',
  'px',
  'py',
  'ps',
  'pe',
  'pt',
  'pr',
  'pb',
  'pl',
  'm',
  'mx',
  'my',
  'ms',
  'me',
  'mt',
  'mr',
  'mb',
  'ml',
  'space-x',
  'space-y',
  'size',
  'w',
  'min-w',
  'max-w',
  'h',
  'min-h',
  'max-h',
  'leading',
  'indent',
  'translate',
  'translate-x',
  'translate-y',
  'scroll-m',
  'scroll-mx',
  'scroll-my',
  'scroll-mt',
  'scroll-mr',
  'scroll-mb',
  'scroll-ml',
  'scroll-p',
  'scroll-px',
  'scroll-py',
  'scroll-pt',
  'scroll-pr',
  'scroll-pb',
  'scroll-pl',
] as const;

const sdSpacingClassGroups = Object.fromEntries(
  SD_SPACING_GROUPS.map(group => [group, [{ [group]: [isSdSpacing] }]]),
);

/**
 * theme.css 의 --text-* 는 기본 Tailwind 폰트사이즈 스케일(xs/sm/base...)이 아닌
 * 숫자 스케일(10~32)이라, 확장 없이는 tailwind-merge 가 이를 텍스트 컬러 그룹으로
 * 오인해 text-12 같은 클래스를 text-(--color-var) 뒤에서 충돌 제거해버린다.
 *
 * --shadow-* 도 마찬가지로 커스텀 이름(normal-sm, spread-md ...)이라 그림자 그룹으로
 * 인식되지 않아, 등록하지 않으면 소비자의 shadow-none 이 내부 그림자를 못 지운다.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      ...sdSpacingClassGroups,
      'font-size': [{ text: ['10', '11', '12', '13', '14', '16', '18', '20', '24', '28', '32'] }],
      'shadow': [
        {
          shadow: [
            'normal-xs',
            'normal-sm',
            'normal-md',
            'spread-sm',
            'spread-md',
            'dialog',
            'floating',
            'focus',
            'overlay',
          ],
        },
      ],
    },
  },
});

/**
 * className 병합 유틸.
 * clsx로 조건부 클래스를 합치고, tailwind-merge로 Tailwind 클래스 충돌을 해소한다.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
